// Used in profile-card.component.html

// Angular
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";

// Services/Types/Helpers
import { LocalStorage } from "#core/services/local-storage.service";
import { FormHelper } from "#helpers/form.helper";
import { PPUser as User } from "cli-types";

// Validators
import { ProfileValidator } from "#validators/profile.validator";

// RXJS/Store
import * as fromCoursesStore from "#core/teacher-classes/store";
import * as fromStore from "#login/store";
import { Auth, UpdateProfileRequest } from "@carnegie-learning/data-access-angular-passport";
import { Store, select } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "change-password-component",
    templateUrl: "./change-password.component.html",
    styleUrls: ["./change-password.component.scss"],
    standalone: false
})
export class ChangePasswordComponent implements OnChanges, OnInit, OnDestroy {
    @Input() profileData?: User;

    isStudent = false;
    isTeacher = false;
    changePasswordMode = false;
    hide1 = true; // Password visibility for current Pwd
    hide2 = true; // Password visibility for new Pwd
    hide3 = true; // Password visibility for confirm Pwd
    showProgressBar = false;

    private ngUnsubscribe = new Subject();

    changePasswordForm = this.fb.group({
        firstName: [""],
        lastName: [""],
        timezone: [""],
        state: [""],
        passwordGroup: this.fb.group(
            {
                // password: [""],
                newPassword: [
                    "",
                    [
                        Validators.required,
                        Validators.minLength(8),
                        Validators.maxLength(128),
                        Validators.pattern(
                            "[^\\w\\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))"
                        ),
                    ],
                ],
                confirmPassword: [
                    "",
                    [
                        Validators.required,
                        Validators.minLength(8),
                        Validators.maxLength(128),
                        Validators.pattern(
                            "[^\\w\\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))"
                        ),
                    ],
                ],
            },
            {
                validator: [
                    ProfileValidator.matchPassword,
                    ProfileValidator.newPassword,
                ],
            }
        ),
    });

    @Output() public processCancel = new EventEmitter<void>();

    constructor(
        private auth: Auth,
        private fb: UntypedFormBuilder,
        public store: Store<fromStore.UserState>,
        public formHelper: FormHelper,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private localStorage: LocalStorage
    ) {}

    ngOnInit() {
        // TODO Refactor this out to container, replace with async validator.
        this.store
            .pipe(select(fromStore.getUpdateProfileError))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data === true && this.showProgressBar) {
                    this.showProgressBar = false;
                    this.cdr.detectChanges();
                }
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.profileData) {
            this.changePasswordForm.patchValue({
                firstName: this.profileData.firstName,
                lastName: this.profileData.lastName,
                timezone: this.profileData.timezone,
                state: this.profileData.state,
            });

            this.isStudent = this.profileData.roleLevel === 5;
            this.isTeacher = this.profileData.roleLevel === 4;
        }

        if (this.isStudent) {
            this.changePasswordForm.get("state")?.disable();
            this.changePasswordForm.get("timezone")?.disable();
        }
    }

    markAsTouched(event: KeyboardEvent) {
        this.changePasswordForm.markAsTouched();
    }

    validate(field: string, type: string) {
        return this.formHelper.Validate(this.changePasswordForm, field, type);
    }

    // password validation chip color
    chipColor(type: string) {
        if (
            !this.changePasswordForm.get("passwordGroup.newPassword")?.touched
        ) {
            // initial state
            return "info";
        }

        const result = this.formHelper.Validate(
            this.changePasswordForm,
            "passwordGroup.newPassword",
            type
        );
        return result ? "info" : "primary";
    }

    disableUpdateButton(): boolean {
        return (
            this.changePasswordForm.invalid || this.changePasswordForm.untouched
        );
    }

    submit() {
        const payload: UpdateProfileRequest = {
            firstName: this.changePasswordForm.get("firstName")?.value,
            lastName: this.changePasswordForm.get("lastName")?.value,
            timezone: this.changePasswordForm.get("timezone")?.value,
            state: this.changePasswordForm.get("state")?.value,
            password: this.changePasswordForm.get("passwordGroup.password")
                ?.value,
            newPassword: this.changePasswordForm.get(
                "passwordGroup.newPassword"
            )?.value,
        };
        this.showProgressBar = true;
        if (
            this.profileData &&
            payload.newPassword &&
            payload.newPassword !== payload.password
        ) {
            this.store.dispatch(
                fromCoursesStore.updateStudentFidoPassword({
                    fidoUserID: this.profileData.userGUID,
                    password: payload.newPassword,
                })
            ); // password update needs to go to FIDO
        }
        this.store.dispatch(
            fromStore.userProfileUpdateAttempt({ payload: payload })
        ); // state and timezone don't go to FIDO. They just go here (pp).
    }

    cancel() {
        // Just route back to the home page; cleanest way to leave the edit profile page.
        this.router.navigate(["teacher/classes/"]);
    }
}
